/**
 * useSupportMessages Hook
 *
 * Admin inbox for contact-form submissions (supportMessages collection).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export type SupportStatus = 'open' | 'resolved';

export interface SupportMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: SupportStatus;
  createdAt: Date;
}

const toDate = (val: unknown): Date =>
  val instanceof Timestamp ? val.toDate() : val ? new Date(val as string) : new Date();

export const useSupportMessages = (filter: SupportStatus | 'all') => {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'supportMessages'), orderBy('createdAt', 'desc'))
        );
        if (cancelled) return;
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name || 'Anonymous',
            email: data.email || '',
            subject: data.subject || '(no subject)',
            message: data.message || '',
            status: (data.status as SupportStatus) || 'open',
            createdAt: toDate(data.createdAt),
          } as SupportMessage;
        });
        setOpenCount(rows.filter((r) => r.status === 'open').length);
        setMessages(filter === 'all' ? rows : rows.filter((r) => r.status === filter));
      } catch (err) {
        console.error('Failed to fetch support messages:', err);
        setMessages([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filter, fetchKey]);

  const setStatus = useCallback(
    async (id: string, status: SupportStatus) => {
      await updateDoc(doc(db, 'supportMessages', id), {
        status,
        updatedAt: serverTimestamp(),
      });
      refetch();
    },
    [refetch]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteDoc(doc(db, 'supportMessages', id));
      refetch();
    },
    [refetch]
  );

  return { messages, openCount, isLoading, setStatus, remove, refetch };
};

export default useSupportMessages;
